import { Link } from 'react-router-dom';
import { Card } from '../kit/Card';
import { formatISTTime } from '../../lib/format';
import { haptic } from '../../lib/haptic';

type TripCardProps = {
  trip: {
    id: string;
    /** Human-readable booking code (e.g. `FL26-SC-3821`). The detail-page
     *  URL builds from this — falling back to `id` keeps the link working
     *  if an old cached record predates the code being included. */
    code?: string | null;
    pickupTime: string;
    pickupLocation?: string | null;
    dropLocation?: string | null;
    status: string;
    client?: { name: string } | null;
    isVip?: boolean;
  };
  /** When true, the client name renders in the Fraunces italic accent
   *  used on HomeRoute's "next trip" hero. Default `false`. */
  accent?: boolean;
  /** When true, the card gets the v2 "next trip" elevation treatment:
   *  mint-line border + shadow-card-elev. Use on the first upcoming trip. */
  isNext?: boolean;
};

export function TripCard({ trip, accent = false, isNext = false }: TripCardProps) {
  const nextOverride = isNext
    ? { borderColor: 'var(--mint-line)', boxShadow: 'var(--shadow-card-elev)' }
    : undefined;
  return (
    <li>
      <Link to={`/trips/${trip.code || trip.id}`} onClick={() => haptic.tap()} className="block">
        <Card className="relative" style={nextOverride}>
          {/* Top row — time pill (with green dot) + optional VIP pill */}
          <div className="flex items-center justify-between gap-2">
            <span
              className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold border"
              style={{
                background: 'var(--mint-soft)',
                color: 'var(--green-700)',
                borderColor: 'var(--mint-line)',
              }}
            >
              <span
                aria-hidden
                className="inline-block rounded-full"
                style={{ width: 4, height: 4, background: 'currentColor' }}
              />
              {formatISTTime(trip.pickupTime)}
            </span>
            {trip.isVip ? (
              <span
                className="inline-flex items-center px-2.5 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider"
                style={{ background: 'var(--color-text)', color: '#F5C97A' }}
              >
                VIP
              </span>
            ) : null}
          </div>

          {/* Client name */}
          <div className="mt-3 text-base font-extrabold text-[var(--color-text)] tracking-tight">
            {accent && trip.client?.name ? (
              <span className="serif-accent text-lg">{trip.client.name}</span>
            ) : (
              trip.client?.name || '—'
            )}
          </div>

          {/* Pickup → drop route viz. Drop is hidden entirely when the booking
              has no dropLocation — the marker rail collapses to just the green
              pickup dot, and the drop row + black square disappear. */}
          {(() => {
            const hasDrop = !!(trip.dropLocation && trip.dropLocation.trim());
            return (
              <div className="flex items-center gap-3 mt-3 min-w-0">
                <svg
                  width="14"
                  height={hasDrop ? 60 : 16}
                  viewBox={`0 0 14 ${hasDrop ? 60 : 16}`}
                  aria-hidden
                  className="flex-shrink-0"
                >
                  <circle cx="7" cy="6" r="6" fill="var(--green-700)" />
                  {hasDrop ? (
                    <>
                      <line
                        x1="7"
                        y1="14"
                        x2="7"
                        y2="46"
                        stroke="var(--color-text-secondary)"
                        strokeWidth="1.5"
                        strokeDasharray="2 3"
                      />
                      <rect x="3" y="48" width="8" height="8" fill="var(--color-text)" />
                    </>
                  ) : null}
                </svg>
                {/* min-w-0 still in place so flex layout doesn't refuse to
                    shrink; addresses now wrap to multiple lines instead of
                    being clipped with ellipsis. */}
                <div className="flex-1 min-w-0 flex flex-col gap-1.5 text-sm py-0.5">
                  <span className="block text-[var(--color-text)] break-words leading-snug">
                    {trip.pickupLocation || '—'}
                  </span>
                  {hasDrop ? (
                    <span className="block text-[var(--color-text-secondary)] break-words leading-snug">
                      {trip.dropLocation}
                    </span>
                  ) : null}
                </div>
              </div>
            );
          })()}
        </Card>
      </Link>
    </li>
  );
}
